import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { NavLink, useLocation } from "react-router-dom";
import { AiOutlineAppstore } from "react-icons/ai";
import SubMenus from "./SubMenu";
import { useMediaQuery } from "react-responsive";
import "../../constants/css/custom_styles.css";
import { MdDevices, MdDomain, MdForum, MdPayments, MdSupportAgent } from "react-icons/md";
import { Bs0Square, BsBox2, BsBoxes, BsGear, BsShop, BsTruck, BsWhatsapp, BsCloudSun, BsChevronLeft, BsList } from "react-icons/bs";
import { BiMobile, BiNotification, BiBrain, BiChip, BiDevices, BiCreditCard } from "react-icons/bi";
import { GoGraph } from "react-icons/go";
import { FaMailBulk, FaProductHunt, FaSatellite, FaStore } from "react-icons/fa";
import { GiFarmer, GiStatic } from "react-icons/gi";
import { CgCommunity } from "react-icons/cg";
import { FaUserGear, FaWhatsapp } from "react-icons/fa6";
import { LuPackage2 } from "react-icons/lu";
import { getUserRole, isB2bSalesperson } from "../../helpers/local_storage_helpers";
import { PiFlagBanner } from "react-icons/pi";
import { TbPhoneCalling, TbScript } from "react-icons/tb";
import { LeaderboardSharp, NotificationImportant, Photo, PhotoAlbumRounded } from "@mui/icons-material";
import { ActivitySquare, History, UserIcon, ListTodo, FileSpreadsheet, Ruler } from "lucide-react";
import { FaBlog, FaTags, FaFolder, FaDownload, FaUsers, FaBuilding, FaHandshake, FaFileInvoice, FaStar } from "react-icons/fa";
import { FiUpload } from "react-icons/fi";

const Sidebar = () => {
  const location = useLocation();
  const userName = localStorage?.getItem("name");
  let isTab = useMediaQuery({ query: "(max-width: 768px)" });
  const [isOpen, setIsOpen] = useState(() => {
    const saved = localStorage?.getItem("sidebar_is_open");
    return saved !== null ? JSON.parse(saved) : (isTab ? false : true);
  });
  const [activeSubMenu, setActiveSubMenu] = useState(null);

  useEffect(() => {
    localStorage?.setItem("sidebar_is_open", JSON.stringify(isOpen));
  }, [isOpen]);

  const [isActive, setIsActive] = useState({
    dashboard: false,
    enquiries: false,
    farmers: false,
    whatsapp: false,
    sales: false,
    store: false,
    satellite: false,
    weather: false,
    chatbot: false,
    aiAnalytics: false,
    leads: false,
    scripts: false,
    supportTickets: false,
    devices: false,
    userOrderHistory: false,
    blog: false,
    sheets: false,
    drive: false,
    downloadSheets: false,
    aiUserChats: false,
    shippingTracking: false,
  });

  useEffect(() => {
    setIsOpen(!isTab);
  }, [isTab]);

  useEffect(() => {
    const path = location.pathname;

    setIsActive({
      dashboard: path === "/dashboard",
      enquiries: path === "/enquiries",
      sales: path.startsWith("/sales"),
      whatsapp: path.startsWith("/whatsapp"),
      store: path.startsWith("/store"),
      notification: path.startsWith("/notification"),
      farmers: path.startsWith("/farmers"),
      satellite: path.startsWith("/satellite"),
      weather: path.startsWith("/weather"),
      chatbot: path.startsWith("/chatbot"),
      aiAnalytics: path.startsWith("/ai-analytics"),
      leads: path.startsWith("/leads"),
      scripts: path.startsWith("/scripts"),
      supportTickets: path.startsWith("/support-tickets"),
      // dukaanUsers: path.startsWith("/dukaan-users"),
      devices: path.startsWith("/devices"),
      userOrderHistory: path.startsWith("/user-order-history"),
      blog: path.startsWith("/blog"),
      sheets: path.startsWith("/sheets"),
      drive: path.startsWith("/drive"),
      downloadSheets: path.startsWith("/download-sheets"),
      aiUserChats: path.startsWith("/ai-user-chats"),
      shippingTracking: path.startsWith("/shipping-tracking"),
    });
  }, [location.pathname]);

  const handleIsActive = (key) => {
    setIsActive((prevState) => ({
      ...prevState,
      [key]: !prevState[key],
    }));
  };

  const staffSubMenus = [];

  // b2b-salesperson: single flat "Leads" list (B2C + B2B + Proforma together, no B2C/B2B sub-grouping).
  // No "Folders" or "B2B Leads" pool access for this role.
  const b2bSalespersonSubMenus = [
    {
      name: "Leads",
      baseRoute: "/leads-management",
      icon: <LeaderboardSharp className="text-lg" />,
      menus: [
        { name: "B2C Leads", icon: <TbPhoneCalling className="text-sm" />, route: "/b2c-sales-person-view" },
        { name: "B2C Interested", icon: <UserIcon className="text-sm" size={14} />, route: "/b2c-interested-leads" },
        { name: "My B2B Leads", icon: <TbPhoneCalling className="text-sm" />, route: "/b2b-sales-person-view" },
        { name: "Marked B2B Leads", icon: <FaStar className="text-sm text-amber-500" />, route: "/b2b-dealers-view" },
        { name: "Paid B2B Dealers", icon: <FaStar className="text-sm text-emerald-500" />, route: "/paid-b2b-dealers-view" },
      ],
    },
    {
      name: "Sales",
      baseRoute: "/sales",
      icon: <GoGraph className="text-lg" />,
      menus: [
        { name: "B2C Orders", icon: <FaUsers className="text-lg" />, route: "/orders" },
        { name: "B2B Orders", icon: <FaBuilding className="text-lg" />, route: "/B2B/orders" },
        { name: "Proforma Invoice", icon: <BiNotification className="text-lg" />, route: "/label-invoice" },
      ],
    },
  ];

  // Staff: flat B2C links only, no submenus. hr-admin: no submenus. B2B salesperson: full B2B + B2C submenus.
  const subMenusList = getUserRole() === "staff" ? staffSubMenus : getUserRole() === "hr-admin" ? [] : isB2bSalesperson() ? b2bSalespersonSubMenus : [
    getUserRole() === "admin" ? {
      name: "WhatsApp",
      baseRoute: "/whatsapp",
      icon: <BsWhatsapp className="text-lg" />,
      menus: [
        { name: "WhatsApp", icon: <BsWhatsapp className="text-lg" />, route: "/whatsapp" },
        { name: "WhatsApp Monitor", icon: <BsWhatsapp className="text-lg text-amber-600" />, route: "/whatsapp/monitor" },
        { name: "WhatsApp Bot", icon: <FaWhatsapp className="text-lg text-green-500" />, route: "/flow-builder" },
      ],
    } : {},
    getUserRole() !== "admin" ? {
      name: "Enquiries",
      baseRoute: "/enquiries-tab",
      icon: <MdSupportAgent className="text-lg" />,
      menus: [
        {
          name: "Product Visitors", icon: <FaProductHunt className="text-lg" />, route: "/enquiries/visitors/products"
        },
        {
          name: "Shop Visitors", icon: <BsShop className="text-lg" />, route: "/enquiries/visitors/shop"
        },
        getUserRole() === "admin" || getUserRole() === "staff" ? {
          name: "FPOs", icon: <NotificationImportant className="text-lg" />, route: "/enquiries/fpos",
        } : {},
        // {
        //   name: "App Enquiries", icon: <BiMobile />, route: "/enquiries/app"
        // },
        {
          name: "Enquiries", icon: <BiMobile className="text-lg" />, route: "/enquiry"
        },
        {
          name: "B2B Enquiries", icon: <FaMailBulk className="text-lg" />, route: "/enquiries/bulk-enquiries"
        },
        {
          name: "B2B Users", icon: <BsShop className="text-lg" />, route: "/bazzar/shop"
        },
      ],
    } : {},
    getUserRole() === "admin" || getUserRole() === "staff" ? {
      name: "Leads",
      baseRoute: "/leads-management",
      icon: <LeaderboardSharp className="text-lg" />,
      menus: [
        {
          name: "B2C",
          icon: <FaUsers className="text-lg" />,
          menus: [
            { name: "Sales Person View", icon: <UserIcon className="text-lg" />, route: "/b2c-sales-person-view" },
            // getUserRole() === "admin" ? {
            //   name: "Master Sheet View", icon: <FileSpreadsheet className="text-lg" />, route: "/b2c-master-sheet-view",
            // } : {},
            // getUserRole() === "admin" ? {
            //   name: "Stats View", icon: <GoGraph className="text-lg" />, route: "/b2c-stats-view",
            // } : {},
            { name: "Interested Leads", icon: <UserIcon className="text-lg" />, route: "/b2c-interested-leads" },
            // getUserRole() === "admin" ? {
            //   name: "Daily call stats", icon: <TbPhoneCalling className="text-lg" />, route: "/b2c-daily-call-stats",
            // } : {},
            getUserRole() === "admin" ? {
              name: "B2C Sales Dashboard", icon: <FaUsers className="text-lg" />, route: "/hr-admin-dashboard",
            } : {},
            getUserRole() === "admin" ? {
              name: "HR Attendance", icon: <FaUsers className="text-lg" />, route: "/hr-attendance",
            } : {},
          ]
        },
        {
          name: "B2B",
          icon: <FaBuilding className="text-lg" />,
          menus: [
            { name: "My B2B Leads", icon: <TbPhoneCalling className="text-sm" />, route: "/b2b-sales-person-view" },
            { name: "My B2B Dealers", icon: <FaStar className="text-sm text-amber-500" />, route: "/b2b-dealers-view" },
            getUserRole() === "admin" ? {
              name: "Paid B2B Dealers", icon: <FaStar className="text-sm text-emerald-500" />, route: "/paid-b2b-dealers-view",
            } : {},
            { name: "Folders", icon: <FaFolder className="text-sm" />, route: "/leads-management/b2b/folders" },
            { name: "B2B Leads", icon: <LeaderboardSharp className="text-sm" />, route: "/leads-management/b2b/leads" },
          ]
        }
      ],
    } : {},
    getUserRole() === "admin" || getUserRole() === "staff" ?
      {
        name: "Sales",
        baseRoute: "/sales",
        icon: <GoGraph className="text-lg" />,
        menus: [
          getUserRole() === "admin" ? {
            name: "Sales Stats", icon: <GoGraph className="text-lg" />, route: "/sales-stats"
          } : {},
          getUserRole() === "admin" ? {
            name: "Product Sales", icon: <GoGraph className="text-lg" />, route: "/product-sales"
          } : {},
          getUserRole() === "admin" ? {
            name: "Ledger",
            icon: <BiCreditCard className="text-lg" />,
            menus: [
              { name: "B2C Ledger", icon: <BiCreditCard className="text-lg" />, route: "/b2c-ledger" },
              { name: "B2B Ledger", icon: <BiCreditCard className="text-lg" />, route: "/b2b-ledger" },
            ],
          } : {},
          getUserRole() === "admin" || getUserRole() === "staff" ? {
            name: "Ecom Enquiries", icon: <MdSupportAgent className="text-lg" />, route: "/ecom-enquiries"
          } : {},
          getUserRole() === "admin" || getUserRole() === "staff" ? {
            name: "B2C Orders", icon: <FaUsers className="text-lg" />, route: "/orders"
          } : {},
          getUserRole() === "admin" || getUserRole() === "staff" ? {
            name: "B2B Orders", icon: <FaBuilding className="text-lg" />, route: "/B2B/orders"
          } : {},
          getUserRole() === "admin" || getUserRole() === "staff" ? {
            name: "Third Party Orders", icon: <FaHandshake className="text-lg" />, route: "/third-party/orders"
          } : {},
          // {
          //   name: "Manage Orders", icon: <BiPackage className="text-lg" />, route: "/manage-orders"
          // },
          getUserRole() === "admin" ? {
            name: "Label Generator", icon: <BiNotification className="text-lg" />, route: "/label-invoice"
          } : {},
          getUserRole() === "admin" ? {
            name: "Proforma Invoice", icon: <BiNotification className="text-lg" />, route: "/proforma-invoice"
          } : {},
          getUserRole() === "admin" || getUserRole() === "staff" ? {
            name: "Shipping Tracking", icon: <BsTruck className="text-lg" />, route: "/shipping-tracking"
          } : {},
        ],
      } : {},
    // getUserRole() === "admin" ? {
    //   name: "Store",
    //   baseRoute: "/store",
    //   icon: <BiStore />,
    //   menus: [
    //     {
    //       name: "Products", icon: <FiPackage />, route: "/products"
    //     },
    //     {
    //       name: "Store Details", icon: <FaStoreAlt />, route: "/details"
    //     },
    //   ],
    // } : {},
    getUserRole() === "admin" ? {
      name: "Bazaar",
      baseRoute: "-",
      icon: <FaStore className="text-lg" />,
      menus: [
        {
          name: "Manufacturer", icon: <FaUserGear className="text-lg" />, route: "/bazzar/manufacturer"
        },
        {
          name: "Domains", icon: <MdDomain className="text-lg" />, route: "/bazzar/domains"
        },
        {
          name: "Products", icon: <LuPackage2 className="text-lg" />, route: "/bazzar/products"
        },
        {
          name: "Product Dimensions", icon: <Ruler className="text-lg" />, route: "/bazzar/shop-products-dimension"
        },
        // {
        //   name: "Primary Products", icon: <LuPackageX />, route: "/bazzar/primary-products"
        // },
      ],
    } : {},
    getUserRole() === "admin" || getUserRole() === "staff" ? {
      name: "Marketing",
      baseRoute: "-",
      icon: <PiFlagBanner className="text-lg" />,
      menus: [
        getUserRole() === "admin" ? {
          name: "Community", icon: <CgCommunity className="text-lg" />, route: "/marketing/community",
        } : {},
        getUserRole() === "admin" || getUserRole() === "staff" ? {
          name: "Forum", icon: <MdForum className="text-lg" />, route: "/marketing/forum",
        } : {},
        getUserRole() === "admin" ? {
          name: "Banners", icon: <Photo className="text-lg" />, route: "/marketing/banners",
        } : {},
        getUserRole() === "admin" ? {
          name: "Mob. Pops", icon: <PhotoAlbumRounded className="text-lg" />, route: "/marketing/mobile-pops",
        } : {},
      ],
    } : {},
    getUserRole() === "admin" ? {
      name: "Extra",
      baseRoute: "-",
      icon: <AiOutlineAppstore className="text-lg" />,
      menus: [
        { name: "Devices", icon: <MdDevices className="text-lg" />, route: "/devices" },
        { name: "Scripts", icon: <TbScript className="text-lg" />, route: "/scripts" },
        {
          name: "Inventory",
          icon: <Bs0Square className="text-md" />,
          menus: [
            { name: "Raw Inv.", icon: <BsBox2 className="text-lg" />, route: "/inventory/raw-items" },
            { name: "Purchase Stock", icon: <BsBoxes className="text-lg" />, route: "/inventory/product-stock" },
            { name: "Manufactured Stock", icon: <Bs0Square className="text-lg" />, route: "/inventory/kit-assembly" },
          ],
        },
        {
          name: "AI",
          icon: <BiBrain className="text-lg" />,
          menus: [
            { name: "Assistant", icon: <BiBrain className="text-lg" />, route: "/chatbot" },
            { name: "Analytics", icon: <BiBrain className="text-lg" />, route: "/ai-analytics" },
            { name: "User Chats", icon: <MdForum className="text-lg" />, route: "/ai-user-chats" },
          ],
        },
        {
          name: "IOT Devices",
          icon: <BiChip className="text-lg" />,
          menus: [
            { name: "IOT Devices", icon: <BiDevices className="text-lg" />, route: "/iot-devices" },
            { name: "IOT Subscription", icon: <BiCreditCard className="text-lg" />, route: "/iot-subscription" },
          ],
        },
        {
          name: "Blog",
          icon: <FaBlog className="text-lg" />,
          menus: [
            { name: "Dashboard", icon: <FaBlog className="text-lg" />, route: "/blog/dashboard" },
            { name: "Posts", icon: <FaBlog className="text-lg" />, route: "/blog/posts" },
            { name: "Categories", icon: <FaFolder className="text-lg" />, route: "/blog/categories" },
            { name: "Tags", icon: <FaTags className="text-lg" />, route: "/blog/tags" },
          ],
        },
        {
          name: "Enquiries",
          icon: <MdSupportAgent className="text-lg" />,
          menus: [
            { name: "Product Visitors", icon: <FaProductHunt className="text-lg" />, route: "/enquiries/visitors/products" },
            { name: "Shop Visitors", icon: <BsShop className="text-lg" />, route: "/enquiries/visitors/shop" },
            { name: "FPOs", icon: <NotificationImportant className="text-lg" />, route: "/enquiries/fpos" },
            { name: "Enquiries", icon: <BiMobile className="text-lg" />, route: "/enquiry" },
            { name: "B2B Enquiries", icon: <FaMailBulk className="text-lg" />, route: "/enquiries/bulk-enquiries" },
            { name: "B2B Users", icon: <BsShop className="text-lg" />, route: "/bazzar/shop" },
          ],
        },
        {
          name: "Satellite",
          icon: <FaSatellite className="text-lg" />,
          menus: [
            { name: "Enquiries", icon: <FaSatellite className="text-lg" />, route: "/satellite/list" },
            { name: "Sat-A Farms", icon: <FaSatellite className="text-lg" />, route: "/satellite/approved-farms" },
            { name: "Dynacrop", icon: <FaSatellite className="text-lg" />, route: "/satellite/dynacrop" },
          ],
        },
      ],
    } : {},
  ];

  return (
    <>
      {/* Mobile Overlay */}
      <AnimatePresence>
        {isTab && isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 z-[998] bg-black/50 backdrop-blur-sm"
          />
        )}
      </AnimatePresence>

      {/* Floating Toggle Button */}
      <div
        className={`fixed top-5 z-[1000] transition-all duration-300 hidden md:block ${isOpen ? "left-[12rem] -ml-3" : "left-4"
          }`}
      >
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="bg-white border-2 border-primary shadow-xl p-1 rounded-full text-primary hover:bg-primary hover:text-white transition-all duration-300 flex items-center justify-center transform hover:scale-110 active:scale-95"
          title={isOpen ? "Close Sidebar" : "Open Sidebar"}
        >
          {isOpen ? <BsChevronLeft size={16} /> : <BsList size={18} />}
        </button>
      </div>

      {/* Mobile Menu Button - always visible in top left on mobile */}
      <div className="md:hidden fixed top-4 left-4 z-[1000]">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="bg-white border border-gray-200 shadow-lg p-2 rounded-lg text-primary"
        >
          <BsList size={24} />
        </button>
      </div>

      <motion.div
        initial={false}
        animate={{
          x: isOpen ? 0 : (isTab ? -300 : -192), // -192 is 12rem
          width: isOpen ? "12rem" : "0rem",
        }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className={`bg-white text-gray-600 shadow-xl h-full overflow-y-auto md:relative fixed z-[999] border-r border-gray-200 ${isOpen ? "pl-2" : "p-0 overflow-hidden"
          }`}
      >
        {/* Logo */}
        <div className={`flex items-center gap-3 font-medium pt-4 px-4 pb-3 transition-opacity duration-200 ${isOpen ? "opacity-100" : "opacity-0"}`}>
          <UserIcon />
          <div className="flex flex-col">
            <span className="text-md uppercase font-bold text-gray-800">
              {getUserRole()}
            </span>
          </div>
        </div>
        <hr className={`border-gray-200 my-1 transition-opacity ${isOpen ? "opacity-100" : "opacity-0"}`} />

        {/* Menus */}
        <div className={`flex flex-col h-full transition-opacity duration-200 ${isOpen ? "opacity-100" : "opacity-0"}`}>
          <ul className="whitespace-pre px-2.5 text-[0.9rem] py-3 flex flex-col gap-1 font-medium h-full">
            {[
              {
                condition: true,
                items: [
                  {
                    path: "/dashboard",
                    icon: <AiOutlineAppstore className="text-lg" />,
                    label: "Dashboard",
                    key: "dashboard"
                  },
                  ...(getUserRole() === "staff" ? [
                    {
                      path: "/b2c-sales-person-view",
                      icon: <TbPhoneCalling className="text-lg" />,
                      label: "B2C Leads",
                      key: "b2c-sales-person-view",
                    },
                    {
                      path: "/b2c-interested-leads",
                      icon: <UserIcon className="text-lg" size={18} />,
                      label: "Interested Leads",
                      key: "b2c-interested-leads",
                    },
                    {
                      path: "/orders",
                      icon: <FaUsers className="text-lg" />,
                      label: "B2C Orders",
                      key: "b2c-orders",
                    },
                    {
                      path: "/B2B/orders",
                      icon: <FaHandshake className="text-lg" />,
                      label: "B2B Orders",
                      key: "b2b-orders",
                    },
                  ] : []),
                  ...(getUserRole() === "hr-admin" ? [
                    {
                      path: "/support-tickets",
                      icon: <MdSupportAgent className="text-lg" />,
                      label: "Support Tickets",
                      key: "supportTickets",
                    },
                    {
                      path: "/shipping-tracking",
                      icon: <BsTruck className="text-lg" />,
                      label: "Shipping",
                      key: "shippingTracking",
                    },
                    {
                      path: "/orders",
                      icon: <FaUsers className="text-lg" />,
                      label: "B2C Orders",
                      key: "b2c-orders",
                    },
                  ] : []),
                ]
              },
              {
                condition: getUserRole() === "admin",
                items: [
                  {
                    path: "/farmers",
                    icon: <GiFarmer className="text-lg" />,
                    label: "Farmers",
                    key: "farmers"
                  },
                ]
              },
              {
                condition: getUserRole() === "admin",
                items: [
                  {
                    path: "/notification",
                    icon: <BiNotification className="text-lg" />,
                    label: "Notification",
                    key: "notification"
                  },
                ]
              },
              {
                condition: getUserRole() === "admin",
                items: [
                  {
                    path: "/support-tickets",
                    icon: <MdSupportAgent className="text-lg" />,
                    label: "Support Tickets",
                    key: "supportTickets"
                  }
                ]
              }
            ].map((group) =>
              group.condition ? (
                group.items.map((item) =>
                  (item.condition === undefined || item.condition) && (
                    <li key={item.key}>
                      <NavLink
                        to={item.path}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${isActive[item.key]
                          ? "bg-primary text-white shadow-md"
                          : "hover:bg-primary/10 hover:text-primary text-gray-600"
                          }`}
                        onClick={() => handleIsActive(item.key)}
                      >
                        {item.icon}
                        <span className="font-medium">{item.label}</span>
                      </NavLink>
                    </li>
                  )
                )
              ) : null
            )}

            {/* Submenus */}
            <div className="mt-1">
              {subMenusList?.map((items, index) => Object.keys(items).length !== 0 ? (
                <div key={index} className="flex flex-col pl-1 pb-1">
                  <SubMenus
                    data={items}
                    isOpen={activeSubMenu === index}
                    toggleSubMenu={() =>
                      setActiveSubMenu(activeSubMenu === index ? null : index)
                    }
                  />
                </div>
              ) : null)}
            </div>
          </ul>
        </div>
      </motion.div>
    </>
  );
};

export default Sidebar;
